import { useEffect, useState } from "react";
import AppRoutes from "./routes";
import TeamsService from "./services/TeamsService";
import { Box, Spinner } from "@chakra-ui/react";
import { Toaster } from "./components/ui/toaster";
import "./i18n";
import { useTranslation } from "react-i18next";
import { normalizeToSupportedLang } from "./services/pkceUtils";
import Login from "./pages/Login";
import { ChoicesProvider } from "./context/ChoicesContext";
import { getAuthorizationCapabilities } from "./api/nova/auth";
import { useIdleLogout } from "./hooks/useIdleLogout";
import {
  clearAuthorizationCapabilities,
  storeAuthorizationCapabilities,
  type AppRole,
} from "./constants/accessRights";

// Return the user to the login page after this much inactivity. Activity keeps
// the session alive; the API client silently refreshes the token before then.
const IDLE_LOGOUT_MS = 30 * 60 * 1000;

// Guard against React StrictMode double-invoking the auth effect, which would
// redeem the single-use OAuth authorization code twice and abort the in-flight
// token exchange.
let authCheckStarted = false;

function App() {
  const { i18n } = useTranslation();
  const selectedLanguage = localStorage.getItem("lang");

  useEffect(() => {
    const lang = selectedLanguage || "en";
    i18n.changeLanguage(lang);
  }, [i18n]);

  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  const hydrateAuthorizationCapabilities = async () => {
    try {
      const capabilities = await getAuthorizationCapabilities();
      console.log("✅ Authorization capabilities received:", capabilities);
      storeAuthorizationCapabilities(capabilities);
      return capabilities;
    } catch (error) {
      console.error("❌ Failed to fetch authorization capabilities:", error);
      // Return a default capability object so the app doesn't crash
      const defaultCapabilities = {
        userId: "unknown",
        email: sessionStorage.getItem("user_name") || "unknown",
        role: (sessionStorage.getItem("user_role") as AppRole) || "Viewer",
        permissions: {},
      };
      storeAuthorizationCapabilities(defaultCapabilities);
      return defaultCapabilities;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      console.log("🔍 App.tsx checkAuth() called on page load");

      // Check for session token and refresh token
      const sessionToken = sessionStorage.getItem("teams_token");
      const refreshToken = sessionStorage.getItem("refresh_token");

      console.log("🔑 Tokens in sessionStorage:", {
        has_session_token: !!sessionToken,
        has_refresh_token: !!refreshToken,
        url_params: window.location.search
      });

      // If no tokens exist, show login page
      if (!sessionToken && !refreshToken) {
        console.log("❌ No tokens found, showing login page");

        // Check if we have OAuth callback params
        const urlParams = new URLSearchParams(window.location.search);
        const hasCode = urlParams.has("code");
        const hasError = urlParams.has("error");

        if (hasCode || hasError) {
          console.log("🔄 OAuth callback detected, attempting authentication");
          try {
            const teamsService = TeamsService.getInstance();
            await teamsService.initialize();
            const token = await teamsService.authenticate();

            if (token) {
              console.log("✅ OAuth callback successful, token acquired");
              sessionStorage.setItem("teams_token", token);
              await hydrateAuthorizationCapabilities();
              setIsAuthenticated(true);
              window.location.replace("/workspace");
              return;
            }
          } catch (callbackError) {
            console.error("❌ OAuth callback failed:", callbackError);
            clearAuthorizationCapabilities();
          }
        }

        setIsAuthenticated(false);
        return;
      }

      // If tokens exist, try to authenticate
      try {
        console.log("✅ Tokens found, validating authentication");
        const teamsService = TeamsService.getInstance();
        await teamsService.initialize();

        const token = await teamsService.authenticate();

        if (token) {
          await hydrateAuthorizationCapabilities();
          setIsAuthenticated(true);
          console.log("✅ Authentication successful");

          try {
            const userInfo = await teamsService.getUserInfo();
            const userLocale = userInfo.lang;

            console.log("🌐 Teams client language:", userLocale);
            const lang = normalizeToSupportedLang(userLocale);
            localStorage.setItem("lang", lang);
            i18n.changeLanguage(lang);
          } catch (error) {
            console.error("Failed to get user info:", error);
          }
        } else {
          console.log("❌ Token validation failed");
          clearAuthorizationCapabilities();
          sessionStorage.removeItem("teams_token");
          sessionStorage.removeItem("refresh_token");
          sessionStorage.removeItem("user_role");
          setIsAuthenticated(false);
        }
      } catch (error) {
        // A failure here (e.g. /me/authorization 401 from an expired token that
        // could not be refreshed) must clear the stale credentials, otherwise the
        // dead token is reused on the next load and the user is stuck on login.
        console.error("❌ Authentication failed:", error);
        clearAuthorizationCapabilities();
        sessionStorage.removeItem("teams_token");
        sessionStorage.removeItem("refresh_token");
        sessionStorage.removeItem("user_role");
        setIsAuthenticated(false);
      }
    };

    if (authCheckStarted) return;
    authCheckStarted = true;
    checkAuth();
  }, [i18n]);

  // After 30 minutes of no interaction, sign out and return to the login page.
  useIdleLogout(isAuthenticated === true, IDLE_LOGOUT_MS, () => {
    TeamsService.getInstance().signOut();
  });

  // DEV Progress visual QA route — no Microsoft login required.
  const isDevPreview =
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/__dev/");

  // Show loading spinner while checking authentication
  if (!isDevPreview && isAuthenticated === null) {
    return (
      <div dir={selectedLanguage === "ar" ? "rtl" : "ltr"}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <Spinner size="xl" />
        </Box>
      </div>
    );
  }

  if (isDevPreview) {
    return (
      <div dir={selectedLanguage === "ar" ? "rtl" : "ltr"}>
        <Toaster />
        <ChoicesProvider>
          <AppRoutes />
        </ChoicesProvider>
      </div>
    );
  }

  return (
    <div dir={selectedLanguage === "ar" ? "rtl" : "ltr"}>
      <Toaster />
      {isAuthenticated ? (
        <ChoicesProvider>
          <AppRoutes />
        </ChoicesProvider>
      ) : (
        <Login />
      )}
    </div>
  );
}

export default App;
